// Đối soát — một trang CHỈ ĐỌC cho mọi đơn vị (Shop, Đại lý, CTV) — GĐ9, §4.6, G12.
// Thay các trang cũ: Đối soát COD có nút "đánh dấu đã nhận", Ví / rút tiền, đối soát Đại lý, CTV.
// ShopTrack nhận COD từ BEST, lập phiếu, duyệt và chuyển khoản; đơn vị chỉ xem:
//  · Thẻ tổng: Đang giao · Chờ BEST thanh toán · Sẵn sàng chi · Trong phiếu · Đã chi tháng này
//    · Tạm giữ (kèm lý do) · Công nợ.
//  · Tab: Đơn · Phiếu đối soát · Công nợ của tôi · (Đại lý/CTV) Công nợ cấp dưới
//    · (Đại lý) Hoa hồng CTV đã trừ.
// API: apps/api/src/routes/reconcile.ts.

import { useSearchParams } from 'react-router-dom'
import type { UseQueryResult } from '@tanstack/react-query'
import { Banknote, CircleCheck, Coins, HandCoins, Hourglass, Lock, PackageOpen, ReceiptText, TriangleAlert, Truck, Users } from 'lucide-react'
import { getAuthState } from '@/lib/auth'
import { money } from '@/lib/format'
import { PageHeader } from '@/components/ui/page-header'
import { KpiTile } from '@/components/ui/kpi-tile'
import { ViewSwitcher, type ViewOption } from '@/components/ui/view-switcher'
import { ReconcileOrders } from '@/components/reconcile/ReconcileOrders'
import { ReconcileVouchers } from '@/components/reconcile/ReconcileVouchers'
import { ReconcileDebt } from '@/components/reconcile/ReconcileDebt'
import { CommissionDeductions, NetworkDebts } from '@/components/reconcile/ReconcileNetwork'
import { KIND_LABEL, sortedKinds, type ReconcileSummary, type UnitRole } from '@/components/reconcile/types'
import { useReconcileSummary } from '@/components/reconcile/useSummary'
import { useUnitCan } from '@/lib/unitAccess'

type Tab = 'orders' | 'vouchers' | 'debt' | 'network-debts' | 'deductions'

export default function ReconcilePage() {
  const auth = getAuthState()
  const me = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const type = me?.tenantType || me?.role
  const role: UnitRole = type === 'agency' ? 'agency' : type === 'ctv' ? 'ctv' : 'shop'
  const [params, setParams] = useSearchParams()
  const summaryQ = useReconcileSummary()
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): mục của cấp dưới cần thêm quyền xem cấp dưới
  const seesNetwork = useUnitCan().can('network.view')

  const options: ViewOption[] = [
    { value: 'orders', label: 'Đơn', icon: PackageOpen, tone: 'blue' },
    { value: 'vouchers', label: 'Phiếu đối soát', icon: ReceiptText, tone: 'violet' },
    { value: 'debt', label: 'Công nợ của tôi', icon: HandCoins, tone: 'orange' },
    ...(role !== 'shop' && seesNetwork
      ? [{ value: 'network-debts', label: 'Công nợ cấp dưới', icon: Users, tone: 'gray' } as ViewOption]
      : []),
    ...(role === 'agency' && seesNetwork
      ? [{ value: 'deductions', label: 'Chiết khấu đã trừ', icon: Coins, tone: 'violet' } as ViewOption]
      : []),
  ]
  const requested = params.get('tab')
  const tab: Tab = (options.some((o) => o.value === requested) ? requested : 'orders') as Tab
  const setTab = (t: string) => setParams(t === 'orders' ? {} : { tab: t }, { replace: true })

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader title="Đối soát" />

      <SummaryTiles q={summaryQ} />

      <ViewSwitcher value={tab} onChange={setTab} options={options} />

      {tab === 'orders' && <ReconcileOrders selfId={auth.activeShopId} role={role} />}
      {tab === 'vouchers' && <ReconcileVouchers />}
      {tab === 'debt' && <ReconcileDebt />}
      {tab === 'network-debts' && role !== 'shop' && <NetworkDebts />}
      {tab === 'deductions' && role === 'agency' && <CommissionDeductions />}
    </div>
  )
}

function SummaryTiles({ q }: { q: UseQueryResult<{ summary: ReconcileSummary }> }) {
  const s = q.data?.summary
  const loading = q.isLoading
  const error = q.isError
  const ready = s?.ready ?? 0
  const heldTotal = (s?.held.debt_recovery ?? 0) + (s?.held.payee_locked ?? 0)
  const readyParts = s ? sortedKinds(s.readyByKind).filter(([, v]) => v > 0) : []
  const heldReasons = s
    ? [
        { label: 'Chờ thu nợ của shop', value: s.held.debt_recovery },
        { label: 'Tài khoản bị khóa', value: s.held.payee_locked },
      ].filter((r) => r.value !== 0)
    : []

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiTile
          tone="blue"
          icon={Truck}
          label="Chờ giao xong"
          value={money(s?.inTransit ?? 0)}
          loading={loading}
          error={error}
          to="/reconcile?stage=in_transit"
        />
        <KpiTile
          tone="gray"
          icon={Hourglass}
          label="Chờ hãng thanh toán"
          value={money(s?.awaitingCarrier ?? 0)}
          loading={loading}
          error={error}
          to="/reconcile?stage=awaiting_carrier"
        />
        <KpiTile
          tone="green"
          icon={CircleCheck}
          label="Sẵn sàng chi"
          value={money(Math.max(0, ready))}
          hint={
            ready < 0
              ? 'Khoản trừ nhiều hơn'
              : readyParts.length > 1
                ? readyParts.map(([k, v]) => `${KIND_LABEL[k] ?? 'Khác'} ${money(v)}`).join(' · ')
                : undefined
          }
          loading={loading}
          error={error}
          to="/reconcile?stage=ready"
        />
        <KpiTile
          tone="violet"
          icon={ReceiptText}
          label="Đã lên phiếu"
          value={money(s?.inVoucher ?? 0)}
          loading={loading}
          error={error}
          to="/reconcile?tab=vouchers"
        />
        <KpiTile
          tone="green"
          icon={Banknote}
          label="Đã chi tháng này"
          value={money(s?.paidThisMonth ?? 0)}
          loading={loading}
          error={error}
          to="/reconcile?tab=vouchers"
          className="col-span-2 lg:col-span-1"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <KpiTile
          tone={heldTotal > 0 ? 'orange' : 'gray'}
          icon={Lock}
          label="Tạm giữ"
          value={money(heldTotal)}
          loading={loading}
          error={error}
          to="/reconcile?stage=held"
        >
          {heldReasons.length > 0 && (
            <ul className="space-y-0.5 text-xs">
              {heldReasons.map((r) => (
                <li key={r.label} className="flex flex-wrap justify-between gap-x-2">
                  <span>{r.label}</span>
                  <b className="font-semibold tabular-nums">{money(r.value)}</b>
                </li>
              ))}
            </ul>
          )}
        </KpiTile>
        <KpiTile
          tone={(s?.debt ?? 0) > 0 ? 'orange' : 'gray'}
          icon={TriangleAlert}
          label="Công nợ"
          value={money(s?.debt ?? 0)}
          loading={loading}
          error={error}
          to="/reconcile?tab=debt"
        />
      </div>
    </div>
  )
}
